import { z } from "zod";

export const WIDGET_THEME_CSS_VAR_NAMES = [
    "--background",
    "--foreground",
    "--card",
    "--card-foreground",
    "--popover",
    "--popover-foreground",
    "--primary",
    "--primary-foreground",
    "--secondary",
    "--secondary-foreground",
    "--muted",
    "--muted-foreground",
    "--accent",
    "--accent-foreground",
    "--destructive",
    "--destructive-foreground",
    "--border",
    "--input",
    "--ring",
    "--radius",
    "--sidebar-background",
    "--sidebar-foreground",
    "--sidebar-primary",
    "--sidebar-primary-foreground",
    "--sidebar-accent",
    "--sidebar-accent-foreground",
    "--sidebar-border",
    "--sidebar-ring",
] as const;

export type WidgetThemeCssVarName = (typeof WIDGET_THEME_CSS_VAR_NAMES)[number];
/** `string | undefined` so API/JSON shapes work with `exactOptionalPropertyTypes`. */
export type WidgetThemeVarsType = {
    [K in WidgetThemeCssVarName]?: string | undefined;
};

export type WebsiteThemeType = {
    variables: WidgetThemeVarsType | null;
    darkVariables: WidgetThemeVarsType | null;
    font: string | null;
};

export const CSS_VALUE_MAX_LENGTH = 120;
export const FONT_FAMILY_MAX_LENGTH = 200;
export const ALLOWED_FONT_FAMILY_CHARS = /^[a-zA-Z0-9\s,\-"'()._]+$/;

/** Lowercased generic font family / common system & web fonts (no remote URLs). */
export const ALLOWED_FONT_FAMILIES = [
    "inter",
    "inter var",
    "system-ui",
    "-apple-system",
    "blinkmacsystemfont",
    "segoe ui",
    "segoe ui variable",
    "roboto",
    "helvetica neue",
    "helvetica",
    "arial",
    "arial black",
    "noto sans",
    "noto serif",
    "noto sans mono",
    "liberation sans",
    "ubuntu",
    "cantarell",
    "fira sans",
    "droid sans",
    "open sans",
    "lato",
    "montserrat",
    "nunito",
    "poppins",
    "raleway",
    "rubik",
    "source sans 3",
    "source sans pro",
    "dm sans",
    "manrope",
    "work sans",
    "plus jakarta sans",
    "figtree",
    "geist",
    "geist sans",
    "ui-sans-serif",
    "sans-serif",
    "ui-serif",
    "serif",
    "georgia",
    "impact",
    "times new roman",
    "times",
    "palatino linotype",
    "palatino",
    "book antiqua",
    "garamond",
    "merriweather",
    "lora",
    "trebuchet ms",
    "verdana",
    "tahoma",
    "ui-monospace",
    "monospace",
    "menlo",
    "monaco",
    "consolas",
    "courier new",
    "lucida console",
    "cascadia code",
    "cascadia mono",
    "jetbrains mono",
    "fira code",
    "source code pro",
    "roboto mono",
    "ibm plex sans",
    "ibm plex mono",
    "sf mono",
    "sfmono-regular",
    "pingfang sc",
    "hiragino sans gb",
    "microsoft yahei",
    "wenquanyi micro hei",
    "apple color emoji",
    "segoe ui emoji",
    "segoe ui symbol",
    "noto color emoji",
    "comic sans ms",
    "chalkboard",
] as const;

const ALLOWED_FONT_FAMILIES_SET = new Set<string>(ALLOWED_FONT_FAMILIES);

export const HSL_TRIPLE_PATTERN = /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/;
export const RADIUS_PATTERN = /^\d{1,3}(\.\d+)?(px|rem|em|%)$/;

const COLOR_CSS_VARIABLES = new Set<WidgetThemeCssVarName>(
    WIDGET_THEME_CSS_VAR_NAMES.filter((name) => name !== "--radius"),
);

export function isWidgetThemeCssVarName(name: string): name is WidgetThemeCssVarName {
    return (WIDGET_THEME_CSS_VAR_NAMES as readonly string[]).includes(name);
}

export function isValidThemeCssValue(name: WidgetThemeCssVarName, value: string): boolean {
    if (!value || value.length > CSS_VALUE_MAX_LENGTH) return false;
    if (name === "--radius") return RADIUS_PATTERN.test(value);
    return COLOR_CSS_VARIABLES.has(name) && HSL_TRIPLE_PATTERN.test(value);
}

export function splitFontFamilyList(value: string): string[] {
    const tokens: string[] = [];
    let current = "";
    let inQuote: '"' | "'" | null = null;
    for (let i = 0; i < value.length; i++) {
        const c = value[i];
        if (inQuote) {
            current += c;
            if (c === inQuote) inQuote = null;
            continue;
        }
        if (c === '"' || c === "'") {
            inQuote = c;
            current += c;
            continue;
        }
        if (c === ",") {
            const t = current.trim();
            if (t) tokens.push(t);
            current = "";
            continue;
        }
        current += c;
    }
    const t = current.trim();
    if (t) tokens.push(t);
    return tokens;
}

function normalizeFontToken(token: string): string {
    return token
        .replace(/^['"]|['"]$/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}

/** Drop unknown families; keep order. Used for parent computed font stacks. */
export function filterAllowedFontFamilyTokens(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > FONT_FAMILY_MAX_LENGTH) return undefined;
    if (!ALLOWED_FONT_FAMILY_CHARS.test(trimmed)) return undefined;
    const parts = splitFontFamilyList(trimmed)
        .map((p) => p.trim())
        .filter(Boolean);
    const kept: string[] = [];
    for (const token of parts) {
        const normalized = normalizeFontToken(token);
        if (normalized && ALLOWED_FONT_FAMILIES_SET.has(normalized)) {
            kept.push(token.replace(/^['"]|['"]$/g, "").trim());
        }
    }
    if (kept.length === 0) return undefined;
    return kept.join(", ");
}

/** Parent override: entire declaration must be allowlisted tokens only. */
export function strictSanitizeFontFamily(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const filtered = filterAllowedFontFamilyTokens(value);
    if (!filtered) return undefined;
    const parts = splitFontFamilyList(filtered);
    const normalizedOriginal = splitFontFamilyList(value.trim())
        .map((p) => normalizeFontToken(p))
        .filter(Boolean);
    if (normalizedOriginal.length !== parts.length) return undefined;
    return filtered;
}

const hslTripleSchema = z.string().regex(HSL_TRIPLE_PATTERN).max(CSS_VALUE_MAX_LENGTH);
const radiusSchema = z.string().regex(RADIUS_PATTERN).max(CSS_VALUE_MAX_LENGTH);

/** Strict: unknown keys and invalid values reject (for manager PATCH). */
export const widgetThemeVariablesSchema = z
    .partialRecord(z.enum(WIDGET_THEME_CSS_VAR_NAMES), z.string().max(CSS_VALUE_MAX_LENGTH))
    .superRefine((vars, ctx) => {
        for (const [name, value] of Object.entries(vars) as [WidgetThemeCssVarName, string | undefined][]) {
            if (value === undefined) continue;
            const ok = name === "--radius"
                ? radiusSchema.safeParse(value).success
                : hslTripleSchema.safeParse(value).success;
            if (!ok) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `invalid value for ${name}`,
                    path: [name],
                });
            }
        }
    });

/** Strict: invalid fonts reject (for manager PATCH). */
export const fontFamilySchema = z
    .string()
    .max(FONT_FAMILY_MAX_LENGTH)
    .superRefine((value, ctx) => {
        if (!strictSanitizeFontFamily(value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "invalid font family",
            });
        }
    })
    .transform((value) => strictSanitizeFontFamily(value)!);
